/**
 * Toast notification container component
 * Renders toasts at the top-right on desktop, bottom-center on mobile
 * Updated to new design schema with smooth animations
 */

import React from 'react';
import { Check, Info, AlertCircle, X, ArrowRight } from 'lucide-react';
import { Toast } from '../../hooks/useToast';

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
  // Minimal design - dark toast with subtle type indicators
  const config = {
    success: {
      icon: Check,
      accentColor: 'bg-emerald-400',
    },
    error: {
      icon: AlertCircle,
      accentColor: 'bg-red-400',
    },
    warning: {
      icon: AlertCircle,
      accentColor: 'bg-amber-400',
    },
    info: {
      icon: Info,
      accentColor: 'bg-gray-400',
    },
  };

  const { accentColor } = config[toast.type];

  const handleActionClick = () => {
    if (toast.action?.onClick) {
      toast.action.onClick();
      onRemove(toast.id);
    }
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-3 bg-gray-900 rounded-xl shadow-xl"
      style={{
        minWidth: toast.action ? '300px' : '240px',
        maxWidth: '400px',
        animation: 'toast-enter 0.3s cubic-bezier(0.21, 1.02, 0.73, 1) forwards'
      }}
    >
      {/* Accent dot */}
      <div
        className={`w-2 h-2 rounded-full ${accentColor} flex-shrink-0`}
        style={toast.type === 'success' ? { animation: 'toast-dot-pulse 0.5s ease-out' } : {}}
      />

      {/* Message */}
      <div className="flex-1 text-[13px] text-white font-medium leading-snug">
        {toast.message}
      </div>

      {/* Action button */}
      {toast.action && (
        <button
          onClick={handleActionClick}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold text-gray-900 bg-white rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors flex-shrink-0"
        >
          {toast.action.label}
          <ArrowRight className="w-3 h-3" />
        </button>
      )}

      {/* Dismiss button */}
      <button
        id='remove-toast-btn'
        onClick={() => onRemove(toast.id)}
        className="w-5 h-5 rounded flex items-center justify-center text-gray-500 hover:text-white transition-colors flex-shrink-0"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
    </div>
  );
};

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <>
      {/* Desktop: top-right */}
      <div className="hidden sm:flex fixed top-4 right-4 z-[9999] flex-col gap-2.5 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto">
            <ToastItem toast={toast} onRemove={onRemove} />
          </div>
        ))}
      </div>

      {/* Mobile: bottom-center with safe area */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-[9999] flex flex-col items-center gap-2 p-4 pb-safe-4 pointer-events-none">
        {toasts.map(toast => (
          <div key={toast.id} className="pointer-events-auto w-full max-w-sm">
            <ToastItem toast={toast} onRemove={onRemove} />
          </div>
        ))}
      </div>

      {/* Toast animations */}
      <style>{`
        @keyframes toast-enter {
          from {
            opacity: 0;
            transform: translateX(12px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes toast-dot-pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.7;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        @media (max-width: 639px) {
          @keyframes toast-enter {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }
      `}</style>
    </>
  );
};
